import React, { useState, useEffect } from 'react';
import { ArrowRight, BarChart3, Building, Calendar, CheckCircle, Database, DollarSign, Eye, FileText, Globe, Home, Instagram, Linkedin, Mail, MapPin, Monitor, Palmtree, PenTool, Play, Shield, Star, Target, TrendingDown, TrendingUp, Users, Waves, Zap } from 'lucide-react';

// Use relative URLs on production (same-origin) to avoid CORS issues
const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

const MegatelLandingPage = () => {
  const [accessGranted, setAccessGranted] = useState(() => {
    return typeof window !== 'undefined' && sessionStorage.getItem('megatel_access') === 'granted';
  });
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [requestName, setRequestName] = useState('');
  const [requestCompany, setRequestCompany] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [calcRent, setCalcRent] = useState(1500);
  const [calcSpend, setCalcSpend] = useState(800);
  const [calcHome, setCalcHome] = useState(350000);
  const [calcShowReport, setCalcShowReport] = useState(false);

  const ACCESS_CODE = 'megatel2026';

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (codeInput.toLowerCase().trim() === ACCESS_CODE.toLowerCase()) {
      sessionStorage.setItem('megatel_access', 'granted');
      setAccessGranted(true);
      setCodeError(false);
    } else {
      setCodeError(true);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!requestName.trim() || !requestEmail.trim()) return;
    setRequestLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: requestName,
          email: requestEmail,
          company: requestCompany || '',
          proposal: 'megatel'
        })
      });
    } catch (err) {
      // Send attempt made regardless
    }
    setRequestSent(true);
    setRequestLoading(false);
  };

  // Load WTS Text Me widget script
  useEffect(() => {
    if (!accessGranted) {
      const existing = document.getElementById('wts-sms-widget-script');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'wts-sms-widget-script';
        script.src = 'https://web-techservices.com/api/widget/lead-capture.js';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [accessGranted]);

  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-slate-800 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">SS</div>
            <h1 className="text-2xl font-bold text-white mb-2">Sierra Scala</h1>
            <p className="text-slate-300 text-sm">Digital Marketing Strategist</p>
          </div>

          {/* Access Code Card */}
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-8 border border-white/40 mb-6" data-testid="access-code-card">
            <h2 className="text-xl font-bold text-white text-center mb-2">This Proposal is Private</h2>
            <p className="text-slate-200 text-sm text-center mb-6">Enter the access code to view the marketing strategy.</p>
            
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={codeInput}
                  onChange={(e) => { setCodeInput(e.target.value); setCodeError(false); }}
                  placeholder="Enter access code"
                  className={`w-full px-4 py-3 bg-white/30 border ${codeError ? 'border-red-500' : 'border-white/50'} rounded-xl text-white placeholder-white/70 focus:outline-none focus:border-blue-400 transition-colors text-center tracking-widest`}
                  data-testid="access-code-input"
                />
                {codeError && <p className="text-red-400 text-xs text-center mt-2" data-testid="access-code-error">Incorrect code. Please try again.</p>}
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all"
                data-testid="access-code-submit"
              >
                View Proposal
              </button>
            </form>
          </div>

          {/* Request Access Card */}
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/35" data-testid="request-access-card">
            {!requestSent ? (
              <>
                <h3 className="text-white font-semibold text-center mb-1">Don't have the code?</h3>
                <p className="text-slate-300 text-xs text-center mb-4">Request access and I'll send it to you directly.</p>
                <form onSubmit={handleRequestAccess} className="space-y-3">
                  <input
                    type="text"
                    value={requestName}
                    onChange={(e) => setRequestName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-blue-400 text-sm"
                    data-testid="request-name-input"
                  />
                  <input
                    type="text"
                    value={requestCompany}
                    onChange={(e) => setRequestCompany(e.target.value)}
                    placeholder="Company (optional)"
                    className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-blue-400 text-sm"
                    data-testid="request-company-input"
                  />
                  <input
                    type="email"
                    value={requestEmail}
                    onChange={(e) => setRequestEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-blue-400 text-sm"
                    data-testid="request-email-input"
                  />
                  <button
                    type="submit"
                    disabled={requestLoading}
                    className="w-full py-2.5 bg-white/30 hover:bg-white/40 border border-white/50 text-white font-medium rounded-lg transition-all text-sm flex items-center justify-center gap-2"
                    data-testid="request-access-submit"
                  >
                    <Mail size={16} /> {requestLoading ? 'Sending...' : 'Request Access Code'}
                  </button>
                </form>
                <div className="mt-4 pt-4 border-t border-white/20 text-center">
                  <p className="text-slate-300 text-xs mb-2">Or reach out directly:</p>
                  <a href="mailto:me@sierrascala.com" className="text-blue-400 hover:text-blue-300 text-sm font-medium" data-testid="direct-email-link">me@sierrascala.com</a>
                </div>
                {/* WTS Text Me Widget */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div
                    id="ss-lead-widget"
                    data-notify="+18174700933"
                    data-page="https://sierrascala.com/megatel"
                    data-webhook={`${BACKEND_URL}/api/contacts`}
                    data-brand="Sierra Scala"
                    data-title="Text Me The Private Access Code"
                    data-testid="sms-widget"
                  ></div>
                </div>
              </>
            ) : (
              <div className="text-center py-4" data-testid="request-sent-confirmation">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="text-green-400" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-1">Request Sent!</h3>
                <p className="text-slate-400 text-sm">I'll send you the access code shortly. Check your email at <strong className="text-white">{requestEmail}</strong>.</p>
              </div>
            )}
          </div>

          {/* Back to portfolio */}
          <div className="text-center mt-6">
            <a href="/" className="text-slate-500 hover:text-slate-300 text-sm flex items-center justify-center gap-1 transition-colors">
              <Home size={14} /> View Full Portfolio
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-white">
      {/* Hero Section */}
      <section className="pt-8 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-slate-700 to-slate-900 rounded-full flex items-center justify-center text-white font-bold text-lg">SS</div>
              <div>
                <h2 className="font-bold text-gray-900">Sierra Scala</h2>
                <p className="text-sm text-gray-600">Digital Marketing Strategist</p>
              </div>
            </div>
            <a href="/" className="text-sm text-slate-600 hover:text-slate-800 font-medium flex items-center gap-1">
              <Home size={16} /> View Full Portfolio
            </a>
          </div>

          {/* Main Hero */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-slate-800 text-white text-sm font-semibold rounded-full mb-6">
              STRATEGIC MARKETING PROPOSAL
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Marketing Plan for{' '}
              <span className="bg-gradient-to-r from-slate-700 to-blue-800 bg-clip-text text-transparent">Megatel Homes</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              A data-driven digital marketing strategy targeting DFW's core homebuyer demographic to amplify Megatel's revolutionary lagoon communities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#strategy" className="px-8 py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-full font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all flex items-center gap-2">
                View Strategy <ArrowRight size={20} />
              </a>
              <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-slate-700 text-slate-700 rounded-full font-semibold hover:bg-slate-50 transition-all">
                Connect on LinkedIn
              </a>
            </div>
          </div>

          {/* DFW Homebuyer Demographics Section */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl shadow-xl p-8 md:p-12 mb-16 text-white">
            <div className="flex items-center gap-3 mb-6">
              <Users className="text-blue-400" size={28} />
              <h2 className="text-2xl font-bold">Understanding Your Buyer: DFW Homebuyer Demographics</h2>
            </div>
            
            <p className="text-slate-300 mb-8">Before choosing platforms, we need to understand who's buying homes in DFW. Here's the data:</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">30-49</div>
                <div className="text-sm text-slate-300">Primary Age Range</div>
                <div className="text-xs text-slate-400 mt-1">Millennials + Gen X</div>
              </div>
              <div className="bg-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">$50K-150K</div>
                <div className="text-sm text-slate-300">Household Income</div>
                <div className="text-xs text-slate-400 mt-1">48% of buyers</div>
              </div>
              <div className="bg-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">$200K-500K</div>
                <div className="text-sm text-slate-300">Price Range Sweet Spot</div>
                <div className="text-xs text-slate-400 mt-1">Megatel's target</div>
              </div>
              <div className="bg-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">70%</div>
                <div className="text-sm text-slate-300">Moving Within Texas</div>
                <div className="text-xs text-slate-400 mt-1">Local targeting key</div>
              </div>
            </div>

            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <Target size={20} className="text-blue-400" /> What This Means for Platform Selection
              </h3>
              <p className="text-slate-300 text-sm">
                <strong className="text-white">Ages 30-49 are most active on Facebook and Instagram</strong>, not TikTok. 57% of Millennial buyers need family financial help—meaning parents (Gen X/Boomers on Facebook) influence purchasing decisions. 
                Our strategy prioritizes platforms where DFW homebuyers actually spend time researching major purchases.
              </p>
            </div>
          </div>

          {/* Company Understanding */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-16 border border-slate-200">
            <div className="flex items-center gap-3 mb-6">
              <Building className="text-slate-700" size={28} />
              <h2 className="text-2xl font-bold text-gray-900">Understanding Megatel Homes</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="text-slate-600" size={20} /> The Megatel Difference
                </h3>
                <p className="text-gray-600 mb-4">
                  Megatel Homes isn't just building houses—they're creating <strong>resort-style living at affordable prices</strong>. Their revolutionary lagoon communities (AnaCapri, Bellagio, Santorini, Venetian, Sicily) feature white sand beaches, crystal-clear lagoons, and amenities that transform everyday living into a vacation.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['DFW', 'Houston', 'Austin', 'San Antonio'].map(city => (
                    <span key={city} className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full">{city}</span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="text-slate-600" size={20} /> The Founders' Story
                </h3>
                <p className="text-gray-600">
                  Brothers <strong>Aaron & Zach Ipour</strong>—immigrants from Cyprus—built Megatel from the ground up. Starting with house flipping in Plano in the early 2000s, they founded Megatel Homes in 2006 and have since completed <strong>100+ developments with thousands of homes</strong>. Their story embodies the American Dream and resonates powerfully with first-time homebuyers.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-slate-700">$309K+</div>
                <div className="text-sm text-gray-600">Starting Prices</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-slate-700">7+</div>
                <div className="text-sm text-gray-600">Lagoon Communities</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-slate-700">2.99%</div>
                <div className="text-sm text-gray-600">Rate Incentive</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-slate-700">100+</div>
                <div className="text-sm text-gray-600">Developments</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Audit Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Website & SEO Analysis</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Current Digital Presence Audit</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">I analyzed megatelhomes.com to identify technical gaps and untapped keyword opportunities worth thousands of monthly searches.</p>
          </div>

          {/* Technical SEO Score Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5 border border-red-200 text-center">
              <div className="text-3xl font-bold text-red-600 mb-1">None</div>
              <div className="text-sm text-gray-700 font-medium">Schema Markup</div>
              <p className="text-xs text-gray-500 mt-2">No LocalBusiness, RealEstateAgent, or Product schema detected</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200 text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-1">Partial</div>
              <div className="text-sm text-gray-700 font-medium">Image Alt Tags</div>
              <p className="text-xs text-gray-500 mt-2">Community & home images missing keyword-rich alt text</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200 text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-1">SPA</div>
              <div className="text-sm text-gray-700 font-medium">Site Architecture</div>
              <p className="text-xs text-gray-500 mt-2">React single-page app limits crawlability without SSR</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5 border border-red-200 text-center">
              <div className="text-3xl font-bold text-red-600 mb-1">No Blog</div>
              <div className="text-sm text-gray-700 font-medium">Content Strategy</div>
              <p className="text-xs text-gray-500 mt-2">No blog or resource content to capture long-tail organic traffic</p>
            </div>
          </div>

          {/* Current Keyword Targeting Analysis */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200 mb-8">
            <h3 className="font-bold text-gray-900 mb-2 text-lg flex items-center gap-2">
              <Target className="text-slate-600" size={22} /> Keywords Megatel Is Currently Targeting
            </h3>
            <p className="text-sm text-gray-500 mb-6">Based on an analysis of megatelhomes.com's title tags, meta descriptions, H1 headings, content, and URL structure — here's what I found:</p>
            
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-2 text-gray-700 font-semibold">Where Found</th>
                    <th className="text-left py-3 px-2 text-gray-700 font-semibold">Keywords Being Targeted</th>
                    <th className="text-left py-3 px-2 text-gray-700 font-semibold">Assessment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Title Tag</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">"New Home Builder in Texas" / "Lagoon Living"</td>
                    <td className="py-3 px-2"><span className="text-yellow-600 text-xs">Broad — competes with every TX builder. "Lagoon Living" is unique but low search volume as a standalone term.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Meta Description</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">"new construction homes in master-planned lagoon communities"</td>
                    <td className="py-3 px-2"><span className="text-green-600 text-xs">Good — "new construction homes" is a high-volume category term. But it's the only meta description site-wide.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">H1 Heading</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">"New Home Builder in Dallas-Fort Worth, Texas"</td>
                    <td className="py-3 px-2"><span className="text-yellow-600 text-xs">Decent geo-targeting but only one H1 for the entire SPA — no city-specific or community-specific H1s.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">URL Structure</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">/communities/dallas-fort-worth-tx/anna/anacapri</td>
                    <td className="py-3 px-2"><span className="text-green-600 text-xs">Smart hierarchy — city + community names in URLs. But SPA rendering may prevent Google from indexing these.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Content Focus</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">"lagoon communities" / "resort-style amenities" / "white sand beaches"</td>
                    <td className="py-3 px-2"><span className="text-yellow-600 text-xs">These are brand differentiators but not what buyers search. People search "new homes anna tx," not "white sand beaches community."</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">ISD References</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Anna ISD, Forney ISD, Princeton ISD, McKinney ISD, Crandall ISD, Dallas ISD</td>
                    <td className="py-3 px-2"><span className="text-green-600 text-xs">Smart — ISD keywords are high-intent for families. But buried in community cards, not in dedicated content.</span></td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Pricing Keywords</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">"2.99% Fixed Rate" / "Starting at $309K-$519K"</td>
                    <td className="py-3 px-2"><span className="text-red-600 text-xs">Great incentive but not SEO-targeted. No pages targeting "homes under 350k DFW" or "low interest rate new homes texas."</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* What's Missing */}
            <div className="bg-white rounded-xl p-5 border border-slate-200 mb-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingDown className="text-red-500" size={18} /> High-Volume Keywords They're NOT Targeting
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { keyword: '"homes for sale anna tx"', vol: '1K-5K/mo', note: 'No dedicated landing page' },
                  { keyword: '"new homes princeton tx"', vol: '500-2K/mo', note: 'Community page only, not city-focused' },
                  { keyword: '"new construction homes DFW"', vol: '1K-10K/mo', note: 'Competing with every builder' },
                  { keyword: '"affordable homes under 400k DFW"', vol: '100-500/mo', note: 'No price-targeted content' },
                  { keyword: '"best neighborhoods anna tx families"', vol: '100-500/mo', note: 'No blog content for this' },
                  { keyword: '"new construction houston under 350k"', vol: '500-2K/mo', note: 'Houston market almost invisible' },
                ].map((item) => (
                  <div key={item.keyword} className="flex items-start gap-2 bg-red-50 rounded-lg p-3 border border-red-100">
                    <TrendingDown size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.keyword}</p>
                      <p className="text-xs text-gray-500">{item.vol} est. — {item.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* GA4 + Search Console Note */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <BarChart3 className="text-blue-600" size={18} /> Want the Full Picture? I Need Access.
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                This audit is based on what's publicly visible. With access to <strong>Google Analytics 4</strong> and <strong>Google Search Console</strong>, I can provide exact data on:
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold mb-1">SEARCH CONSOLE</p>
                  <p className="text-sm text-gray-700">Exact keywords driving impressions & clicks, average positions, CTR by query, and pages losing traffic</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold mb-1">GA4 ANALYTICS</p>
                  <p className="text-sm text-gray-700">Which pages convert visitors to leads, traffic sources breakdown, user behavior flow, and bounce rates by landing page</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold mb-1">GOOGLE ADS</p>
                  <p className="text-sm text-gray-700">Current CPC, cost per lead, which campaigns are performing, wasted spend, and quality score opportunities</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 italic">A 30-minute review of these dashboards would let me build a pinpoint strategy with real numbers instead of estimates.</p>
            </div>
          </div>

          {/* Long-Tail Strategy: DFW Homes Blog */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-6 border border-blue-200">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <PenTool className="text-blue-600" size={20} /> DFW Homes Blog as SEO Generator
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                A blog targeting long-tail keywords is the single highest-ROI SEO strategy for homebuilders. 70% of real estate searches use long-tail phrases. Each blog post becomes a permanent organic traffic source.
              </p>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold mb-1">EXAMPLE POST</p>
                  <p className="text-sm font-medium text-gray-800">"Best School Districts Near Anna TX for Families (2025)"</p>
                  <p className="text-xs text-gray-500">Targets: anna tx schools, anna isd homes, best neighborhoods anna tx</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold mb-1">EXAMPLE POST</p>
                  <p className="text-sm font-medium text-gray-800">"What Does Resort-Style Living Actually Cost in Texas?"</p>
                  <p className="text-xs text-gray-500">Targets: lagoon homes texas cost, resort community affordable, megatel pricing</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold mb-1">EXAMPLE POST</p>
                  <p className="text-sm font-medium text-gray-800">"Princeton TX vs. Celina TX: Which DFW Suburb Is Right for You?"</p>
                  <p className="text-xs text-gray-500">Targets: princeton vs celina, best dfw suburbs, new homes princeton tx</p>
                </div>
              </div>
              <div className="mt-4 bg-blue-600 text-white rounded-lg p-3">
                <p className="text-xs font-semibold mb-1">THE PLAY</p>
                <p className="text-sm">Publish 2-4 posts/month with AI-assisted content. Each post targets 3-5 long-tail keywords (100-500 searches/mo each). Within 6 months, this captures thousands of organic searches that currently go to competitors like Zillow and Realtor.com.</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-slate-50 rounded-2xl p-6 border border-green-200">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="text-green-600" size={20} /> Geo-Targeted Landing Pages
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Megatel builds in 15+ cities across Texas but has no dedicated landing pages per city. Each geo-page can rank for "[city] new homes" keywords (500-5,000 searches/mo per city).
              </p>
              <div className="space-y-2 mb-4">
                {[
                  { city: 'Anna, TX', vol: '1K-5K/mo', status: 'No dedicated page' },
                  { city: 'Princeton, TX', vol: '500-2K/mo', status: 'No dedicated page' },
                  { city: 'Celina, TX', vol: '500-2K/mo', status: 'No dedicated page' },
                  { city: 'Forney, TX', vol: '1K-5K/mo', status: 'No dedicated page' },
                  { city: 'Huffman, TX', vol: '100-500/mo', status: 'No dedicated page' },
                  { city: 'San Antonio area', vol: '5K-10K/mo', status: 'No dedicated page' },
                ].map((item) => (
                  <div key={item.city} className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-100">
                    <span className="text-sm font-medium text-gray-800">{item.city}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">{item.vol}</span>
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{item.status}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-green-600 text-white rounded-lg p-3">
                <p className="text-xs font-semibold mb-1">THE PLAY</p>
                <p className="text-sm">Create a unique page per city/community with local content: school districts, commute times, neighborhood features, available homes. Each page becomes a landing destination for both organic search and paid geo-targeted ads. Case studies show this approach drives 51-153% traffic growth for homebuilders.</p>
              </div>
            </div>
          </div>

          {/* Technical Recommendations */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <CheckCircle className="text-blue-400" size={20} /> Technical SEO & Content Roadmap
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Launch DFW Homes Blog</p>
                    <p className="text-sm text-slate-400">Long-tail keyword engine: 2-4 AI-assisted posts/month targeting "best neighborhoods in [city]," "homes near [ISD]," cost-of-living comparisons</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Build Geo-Targeted City Pages</p>
                    <p className="text-sm text-slate-400">One page per city/community: "New Homes in Anna TX - Anna ISD" with local keywords, maps, school info, and available listings</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Add LocalBusiness Schema Markup</p>
                    <p className="text-sm text-slate-400">Structured data for each community so Google displays rich results: prices, locations, ratings</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">4</span>
                  </div>
                  <div>
                    <p className="font-medium">Optimize Image Alt Tags</p>
                    <p className="text-sm text-slate-400">Keyword-rich descriptions on all community photos: "Lagoon pool at AnaCapri community Anna TX"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">5</span>
                  </div>
                  <div>
                    <p className="font-medium">Google Business Profile per Community</p>
                    <p className="text-sm text-slate-400">Verified GBP listing with photos, posts, and reviews for each lagoon community to dominate local pack</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">6</span>
                  </div>
                  <div>
                    <p className="font-medium">SSR or Pre-Rendering for SPA</p>
                    <p className="text-sm text-slate-400">The React SPA architecture limits crawlability. Server-side rendering or pre-rendering ensures Google indexes all pages and content</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-300"><strong className="text-white">Bottom line:</strong> Megatel's communities are the product — but without a blog, geo-pages, and structured data, Google can't surface them to the thousands of people searching every month. These changes turn the website from a brochure into a lead-generation machine.</p>
            </div>
          </div>

          {/* YouTube Video SEO Audit */}
          <div className="mt-8 bg-gradient-to-br from-red-50 to-slate-50 rounded-2xl p-6 md:p-8 border border-red-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Play className="text-white" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">YouTube & Video SEO Audit</h3>
                <p className="text-xs text-gray-500">@megatelgroup — 363 subscribers, 124 videos</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6">Megatel has a library of 124 videos showcasing their lagoon communities — but almost none of them are optimized for search. This is a massive untapped SEO asset.</p>

            {/* Current Problems */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl p-5 border border-red-100">
                <h4 className="font-bold text-red-600 text-sm mb-3 flex items-center gap-2">
                  <TrendingDown size={16} /> What's Wrong Now
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Vague, non-searchable titles</p>
                      <p className="text-xs text-gray-500">Current: "Lagoon Living with Megatel" — No city, no price, no keywords anyone searches</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Empty or minimal descriptions</p>
                      <p className="text-xs text-gray-500">Short promo text with no keywords, timestamps, community links, or CTAs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">No tags or keyword targeting</p>
                      <p className="text-xs text-gray-500">Missing location tags like "new homes Anna TX," "DFW home builder," "lagoon community Texas"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Third-party creators outranking Megatel</p>
                      <p className="text-xs text-gray-500">Realtors like "Zak Schmidt" and "DFW Homes by Keonna" rank higher for Megatel searches than Megatel's own channel</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-green-100">
                <h4 className="font-bold text-green-600 text-sm mb-3 flex items-center gap-2">
                  <TrendingUp size={16} /> What I'd Fix
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Keyword-rich titles with location + price</p>
                      <p className="text-xs text-gray-500">Example: "New Homes in Anna TX from $309K | AnaCapri Lagoon Tour | Megatel Homes"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Full descriptions with timestamps & links</p>
                      <p className="text-xs text-gray-500">First 150 chars: keywords. Add timestamps, community page links, pricing, and "Schedule a Tour" CTA</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">5-10 targeted tags per video</p>
                      <p className="text-xs text-gray-500">"Megatel Homes," "new homes Anna TX," "lagoon community DFW," "homes under 400k Texas"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Custom thumbnails with text overlays</p>
                      <p className="text-xs text-gray-500">Community name, price point, "Virtual Tour" badge — consistent brand template</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Before/After Examples */}
            <div className="bg-white rounded-xl p-5 border border-slate-200 mb-6">
              <h4 className="font-bold text-gray-900 text-sm mb-4">Before & After: Title Optimization Examples</h4>
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <p className="text-xs text-red-500 font-semibold mb-1">CURRENT TITLE</p>
                    <p className="text-sm text-gray-700">"Lagoon Living with Megatel | SIX MONTHS MORTGAGE ON US"</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-xs text-green-600 font-semibold mb-1">OPTIMIZED TITLE</p>
                    <p className="text-sm text-gray-700">"New Homes in DFW from $309K | Lagoon Community Tour + 6 Months FREE Mortgage | Megatel Homes"</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <p className="text-xs text-red-500 font-semibold mb-1">CURRENT TITLE</p>
                    <p className="text-sm text-gray-700">"Lagoon Living in DFW - Megatel Homes | Laguna Azure"</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-xs text-green-600 font-semibold mb-1">OPTIMIZED TITLE</p>
                    <p className="text-sm text-gray-700">"Laguna Azure Community Tour | New Homes in Celina TX | Resort-Style Living from $350K"</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <p className="text-xs text-red-500 font-semibold mb-1">CURRENT TITLE</p>
                    <p className="text-sm text-gray-700">"Home ownership just got smarter."</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-xs text-green-600 font-semibold mb-1">OPTIMIZED TITLE</p>
                    <p className="text-sm text-gray-700">"How to Buy a New Home in DFW for Under $400K (2026) | Megatel Homes Buyer Guide"</p>
                  </div>
                </div>
              </div>
            </div>

            {/* The Opportunity */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-5 text-white">
              <h4 className="font-bold mb-2">The Opportunity: 124 Videos = 124 SEO Pages</h4>
              <p className="text-sm text-red-100 mb-3">Every YouTube video is a Google-indexed page. Megatel has 124 of them sitting there with almost zero SEO value. Here's the play:</p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-white/15 rounded-lg p-3">
                  <p className="font-bold text-lg">Phase 1</p>
                  <p className="text-xs text-red-100">Re-optimize all 124 existing video titles, descriptions, and tags. Zero production cost — just metadata updates. Immediate impact.</p>
                </div>
                <div className="bg-white/15 rounded-lg p-3">
                  <p className="font-bold text-lg">Phase 2</p>
                  <p className="text-xs text-red-100">Create YouTube Shorts from existing long-form content. One lagoon tour = 5-10 Shorts targeting different keywords. Massive reach boost.</p>
                </div>
                <div className="bg-white/15 rounded-lg p-3">
                  <p className="font-bold text-lg">Phase 3</p>
                  <p className="text-xs text-red-100">Embed optimized videos on geo-targeted city pages and blog posts. Cross-pollinate website SEO with YouTube SEO for compound growth.</p>
                </div>
              </div>
              <p className="text-xs text-red-200 mt-3 italic">Realtors are already making Megatel content that outranks Megatel. It's time Megatel owned their own search results.</p>
            </div>
          </div>

          {/* Web-TechServices Partner Note */}
          <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Waves className="text-white" size={20} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">A Note on Execution: Tools That Can Scale This</h4>
                <p className="text-sm text-gray-600 mb-4">
                  I partner with <a href="https://web-techservices.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-semibold hover:underline">Web-TechServices.com</a>, a DFW-based AI-powered web and SEO agency (serving DFW businesses since 2000), whose packages align directly with what Megatel needs:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-indigo-100">
                    <h5 className="font-bold text-indigo-700 text-sm mb-2">Turbo SEO (AI-Powered)</h5>
                    <ul className="space-y-1.5 text-xs text-gray-600">
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Unlimited AI keyword discovery — scans trending local searches</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Auto-generated SEO-optimized landing pages per city/community</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Hyper-local geo-targeting by ZIP code</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Daily AI content generation (blog + long-tail keywords)</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Full programmatic SEO implementation</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-2 italic">This is exactly the geo-page + blog engine described above — pre-built and automated.</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-indigo-100">
                    <h5 className="font-bold text-indigo-700 text-sm mb-2">Social Builder Package</h5>
                    <ul className="space-y-1.5 text-xs text-gray-600">
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Automated keyword-rich posts across 3+ platforms</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> AI-generated captions & scheduling</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Content calendar automation (Facebook, Instagram, etc.)</li>
                      <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-indigo-500 mt-0.5" /> Drives real traffic — not vanity metrics</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-2 italic">Pairs with the Facebook/Instagram strategy to automate posting at scale.</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">These tools would dramatically accelerate Megatel's SEO and social presence without requiring a large in-house team. I can manage the strategy and content direction while the platform handles the heavy lifting.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Section */}
      <section id="strategy" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Proposed Strategy</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Platform Strategy (Prioritized by Demographics)</h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Channels ordered by where DFW homebuyers (ages 30-49, $50K-150K income) actually research and make decisions.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Strategy 1 - Facebook (PRIMARY) */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-blue-500/50 hover:bg-white/15 transition-all relative">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                #1 PRIORITY
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mb-4 mt-2">
                <Users className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Facebook & Facebook Groups</h3>
              <p className="text-blue-300 text-xs mb-3">73% of 30-50 year olds use Facebook daily</p>
              <p className="text-slate-400 mb-4">The #1 platform for DFW homebuyers researching neighborhoods, school districts, and communities.</p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Targeted ads by ZIP code, income, life events (engaged, new job)</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Community-specific Facebook Groups engagement</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Auto-posting new listings with AI-generated captions</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Virtual tour events and live Q&As</li>
              </ul>
            </div>

            {/* Strategy 2 - Google PPC */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-green-500/50 hover:bg-white/15 transition-all relative">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                #2 PRIORITY
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 mt-2">
                <Target className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Google PPC (High-Intent)</h3>
              <p className="text-green-300 text-xs mb-3">Captures buyers actively searching "new homes DFW"</p>
              <p className="text-slate-400 mb-4">When someone Googles "lagoon communities Texas" or "new homes Anna TX," they're ready to buy.</p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Geo-targeted campaigns by community & school district</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> 2.99% rate incentive in ad copy</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Landing pages for each lagoon community</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Call tracking & conversion optimization</li>
              </ul>
            </div>

            {/* Strategy 3 - Instagram */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-pink-500/50 hover:bg-white/15 transition-all relative">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-pink-500 text-white text-xs font-bold rounded-full">
                #3 PRIORITY
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 mt-2">
                <Instagram className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Instagram (Visual Storytelling)</h3>
              <p className="text-pink-300 text-xs mb-3">62% of 25-44 year olds use Instagram</p>
              <p className="text-slate-400 mb-4">Perfect for showcasing the lagoon lifestyle with high-quality visuals and Reels.</p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Drone footage of lagoons & white sand beaches</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Before/after home transformation Reels</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Community spotlight by school district</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Auto-generated content calendar</li>
              </ul>
            </div>

            {/* Strategy 4 - Content Automation */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-12 h-12 bg-gradient-to-r from-slate-600 to-slate-700 rounded-xl flex items-center justify-center mb-4">
                <Monitor className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Auto-Posting & Content Generation</h3>
              <p className="text-slate-400 mb-4">Scale content across platforms with AI-powered automation.</p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> AI-generated listing descriptions</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Scheduled posts across Facebook, Instagram</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Auto-generated video captions & hashtags</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Weekly content calendar automation</li>
              </ul>
            </div>

            {/* Strategy 5 - Founder Story */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-12 h-12 bg-gradient-to-r from-slate-600 to-slate-700 rounded-xl flex items-center justify-center mb-4">
                <Star className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Founder Story Content</h3>
              <p className="text-slate-400 mb-4">"From Renter to Resort Owner"—the Ipour brothers' American Dream story.</p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Video series: immigrant success story</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Behind-the-scenes builder tours</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Rent-back program education content</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-400 mt-0.5" /> Humanizes brand, builds trust</li>
              </ul>
            </div>

            {/* Let's Evaluate Together */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-2xl p-6 text-white border border-slate-600">
              <h3 className="text-xl font-bold mb-3">Before I Project Numbers...</h3>
              <p className="text-slate-300 text-sm mb-4">I don't want to throw out projections without real data. To give you an accurate forecast, I'd need to understand:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">?</span>
                  </div>
                  <p className="text-sm text-slate-300">Current monthly ad budget (Google Ads, Meta, etc.)</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">?</span>
                  </div>
                  <p className="text-sm text-slate-300">Current cost per click and cost per lead</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">?</span>
                  </div>
                  <p className="text-sm text-slate-300">Access to GA4, Search Console & Google Ads accounts</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">?</span>
                  </div>
                  <p className="text-sm text-slate-300">Monthly lead targets and sales close rate</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-600">
                <p className="text-xs text-slate-400">Give me 30 minutes with your dashboards and I'll build a projection you can actually trust — not guesswork.</p>
              </div>
            </div>
          </div>

          {/* TikTok Note */}
          <div className="mt-8 bg-white/5 rounded-xl p-6 border border-white/10">
            <h4 className="font-bold text-white mb-2 flex items-center gap-2">
              <Play size={18} className="text-slate-400" /> Why TikTok Isn't #1 (But Still Valuable)
            </h4>
            <p className="text-slate-400 text-sm">
              TikTok's core demo (18-29) represents only ~15% of DFW homebuyers. However, it's valuable for <strong className="text-white">brand awareness</strong> and reaching future buyers. 
              We'd use it for viral lagoon content and "aspirational lifestyle" videos—but ad spend goes where buyers actually convert: Facebook and Google.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TECHNICAL SEO — SCHEMA STACKING & MAP STACKING */}
      {/* ═══════════════════════════════════════════════════════════ */}

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 relative overflow-hidden" data-testid="megatel-technical-seo">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 25% 40%, #6366f1 0%, transparent 50%), radial-gradient(circle at 75% 60%, #10b981 0%, transparent 50%)'}} />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/20 border border-indigo-500/30 rounded-full mb-4">
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
              <span className="text-indigo-300 text-xs font-semibold uppercase tracking-wider">Technical SEO Extensions</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Schema Stacking & Map Stacking</h2>
            <p className="text-indigo-200 max-w-3xl mx-auto">Two high-ROI technical SEO moves that amplify every other channel — paid ads perform better when organic local signals are strong. Low ongoing cost, high long-term leverage.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Schema Stacking */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-indigo-400/30 hover:border-indigo-400/50 transition-all">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <Database size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Schema Stacking</h3>
                  <span className="text-xs text-green-400 font-semibold px-2 py-0.5 bg-green-500/20 rounded-full">White-Hat & High-Impact</span>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">Layering multiple Schema.org types on the same page (Organization + LocalBusiness + RealEstateListing + FAQ + Review + Place) in clean JSON-LD. Google explicitly supports this — and rewards it.</p>

              <h4 className="text-indigo-300 font-semibold text-sm mb-3">Why It's Perfect for Megatel:</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" /> Strengthens entity understanding for Google/AI search — critical with generative results</li>
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" /> Enables rich results: pricing, availability, ratings, "near me" carousels, enhanced community listings</li>
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" /> Perfect for Megatel's site structure — community pages, home plans, quick move-ins</li>
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" /> Boosts local SEO signals across multiple Texas markets simultaneously</li>
              </ul>

              <h4 className="text-indigo-300 font-semibold text-sm mb-3">Implementation per Page:</h4>
              <div className="space-y-2">
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <code className="text-xs text-indigo-300 font-mono bg-indigo-900/50 px-2 py-1 rounded">Homepage</code>
                  <span className="text-xs text-slate-400">Organization + LocalBusiness</span>
                </div>
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <code className="text-xs text-indigo-300 font-mono bg-indigo-900/50 px-2 py-1 rounded">Community Pages</code>
                  <span className="text-xs text-slate-400">LocalBusiness + Place + RealEstateListing + Review + FAQ</span>
                </div>
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <code className="text-xs text-indigo-300 font-mono bg-indigo-900/50 px-2 py-1 rounded">Home Plans</code>
                  <span className="text-xs text-slate-400">Product + Offer + BreadcrumbList</span>
                </div>
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <code className="text-xs text-indigo-300 font-mono bg-indigo-900/50 px-2 py-1 rounded">Quick Move-Ins</code>
                  <span className="text-xs text-slate-400">RealEstateListing + Offer + Residence</span>
                </div>
              </div>

              <div className="mt-6 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-green-300 text-xs font-medium">Expected Impact: Higher click-through rates on community/plan pages. Better performance in real-estate rich snippets. One of the highest-ROI technical SEO moves for home builders right now.</p>
              </div>
            </div>

            {/* Map Stacking */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-emerald-400/30 hover:border-emerald-400/50 transition-all">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <MapPin size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Map Stacking</h3>
                  <span className="text-xs text-blue-400 font-semibold px-2 py-0.5 bg-blue-500/20 rounded-full">Local Pack Domination</span>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">Creating an interlinked "stack" of Google-owned properties (Maps embeds, Docs, Sites, Sheets with location data) all pointing back to each community's page and GBP. Signals relevance and authority to Google's local algorithm.</p>

              <h4 className="text-emerald-300 font-semibold text-sm mb-3">Why It's Perfect for Megatel:</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> Multiple physical locations with sales centers — prime candidate for dominating local Map Pack in DFW</li>
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> Home buyers heavily use Maps when researching ("directions to AnaCapri Anna TX")</li>
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> Builds local authority faster than traditional citations alone</li>
                <li className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> Pairs with optimized GBP per community (central + location extensions)</li>
              </ul>

              <h4 className="text-emerald-300 font-semibold text-sm mb-3">Stack per Community:</h4>
              <div className="space-y-2">
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
                  <span className="text-xs text-slate-300">Custom Google Map with community pin + service area embedded on site footer/contact page</span>
                </div>
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                  <span className="text-xs text-slate-300">Google Site/Doc/Sheet/Drive folder — keyword-optimized with NAP, services, community details</span>
                </div>
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold">3</div>
                  <span className="text-xs text-slate-300">Interlink all stack assets to each other + community page + GBP for authority signals</span>
                </div>
                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold">4</div>
                  <span className="text-xs text-slate-300">Add media (photos, video tours) + embedded GBP reviews. Roll out community-by-community.</span>
                </div>
              </div>

              <div className="mt-6 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-300 text-xs font-medium">Caveat: Clean, ethical implementation only — real embeds on relevant pages, consistent NAP across communities. Avoid anything automated/spammy. Done right, this produces the fastest Map Pack jumps.</p>
              </div>
            </div>
          </div>

          {/* How They Amplify Everything */}
          <div className="mt-10 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <h3 className="text-white font-bold text-center mb-6">How These Amplify Your Entire Marketing Strategy</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-10 h-10 bg-indigo-500/30 rounded-lg flex items-center justify-center mx-auto mb-2"><TrendingUp size={18} className="text-indigo-300" /></div>
                <p className="text-white text-sm font-medium">PPC Boost</p>
                <p className="text-xs text-slate-400">Paid ads perform better when organic local signals are strong</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-emerald-500/30 rounded-lg flex items-center justify-center mx-auto mb-2"><MapPin size={18} className="text-emerald-300" /></div>
                <p className="text-white text-sm font-medium">Map Pack Wins</p>
                <p className="text-xs text-slate-400">Track local pack rankings per community and per keyword</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center mx-auto mb-2"><Eye size={18} className="text-blue-300" /></div>
                <p className="text-white text-sm font-medium">Rich Snippets</p>
                <p className="text-xs text-slate-400">Schema-rich result impressions measured in GSC</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-amber-500/30 rounded-lg flex items-center justify-center mx-auto mb-2"><DollarSign size={18} className="text-amber-300" /></div>
                <p className="text-white text-sm font-medium">Low Cost, High ROI</p>
                <p className="text-xs text-slate-400">Implement quickly, minimal ongoing cost, compounds over time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* MEGPRIME STRATEGY SECTIONS */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* MegPrime Transition Banner */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0a1628] via-[#122244] to-[#0a1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 30% 50%, #c9a44a 0%, transparent 50%), radial-gradient(circle at 70% 50%, #3b82f6 0%, transparent 50%)'}} />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#c9a44a]/20 border border-[#c9a44a]/40 rounded-full mb-6">
            <div className="w-2.5 h-2.5 bg-[#c9a44a] rounded-full animate-pulse" />
            <span className="text-[#c9a44a] text-sm font-semibold tracking-wider uppercase">New Venture Strategy</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            MegPrime<span className="text-[#c9a44a]">Pay</span> Marketing Strategy
          </h2>
          <p className="text-xl text-blue-200 max-w-3xl mx-auto mb-6">
            The only payments token backed by the SEC that turns your rent, bills, and daily spends into lower mortgage rates and real home equity — while you live the lagoon lifestyle at Megatel Homes.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <span className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white text-sm font-medium">SEC No-Action Relief</span>
            <span className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white text-sm font-medium">Utility Token</span>
            <span className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white text-sm font-medium">Homeownership Accelerator</span>
            <span className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white text-sm font-medium">Megatel Integration</span>
          </div>
        </div>
      </section>

      {/* MegPrime Key Pillars */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="megprime-pillars">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c9a44a] font-semibold text-sm uppercase tracking-wider">Brand Positioning</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Four Pillars of MegPrime Content</h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">Every piece of content maps back to one of these pillars — building trust, demonstrating utility, and driving waitlist conversions.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-b from-blue-50 to-white rounded-2xl p-6 border border-blue-100 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <Shield className="text-white" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Regulatory Leadership</h3>
              <p className="text-sm text-gray-600 mb-3">Lead with the SEC No-Action milestone — trust + education content.</p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-500 mt-0.5 flex-shrink-0" /> "What SEC No-Action means for you" explainer videos</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-500 mt-0.5 flex-shrink-0" /> Compliance = safety messaging for risk-averse families</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-500 mt-0.5 flex-shrink-0" /> Differentiation from speculative crypto</li>
              </ul>
            </div>
            <div className="bg-gradient-to-b from-[#fdf6e3] to-white rounded-2xl p-6 border border-[#c9a44a]/20 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-[#c9a44a] rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="text-white" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Everyday Utility</h3>
              <p className="text-sm text-gray-600 mb-3">"Pay for coffee, earn MP, buy a home faster."</p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-[#c9a44a] mt-0.5 flex-shrink-0" /> Real-spend-to-reward Reels (coffee, groceries, rent)</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-[#c9a44a] mt-0.5 flex-shrink-0" /> MegPrime Card showcase content</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-[#c9a44a] mt-0.5 flex-shrink-0" /> BillPay + P2P feature demos</li>
              </ul>
            </div>
            <div className="bg-gradient-to-b from-green-50 to-white rounded-2xl p-6 border border-green-100 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-4">
                <Home className="text-white" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Homeownership Accelerator</h3>
              <p className="text-sm text-gray-600 mb-3">RentForward + MegaLow Rate = "rent-to-own 2.0" hook.</p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" /> RentForward progress bar animations (rent to $25K equity)</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" /> MegaLow Rate savings calculator content</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" /> HomePath + Builders Incentive education</li>
              </ul>
            </div>
            <div className="bg-gradient-to-b from-pink-50 to-white rounded-2xl p-6 border border-pink-100 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                <Palmtree className="text-white" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Lifestyle Synergy</h3>
              <p className="text-sm text-gray-600 mb-3">Tie every reward to Megatel's lagoon communities.</p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-pink-500 mt-0.5 flex-shrink-0" /> "Beach days funded by rewards" lifestyle shoots</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-pink-500 mt-0.5 flex-shrink-0" /> Lagoon community + MegPrime pairing content</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-pink-500 mt-0.5 flex-shrink-0" /> Aspirational "spend to live here" narratives</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MegPrime Target Audience */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0a1628] to-[#122244]" data-testid="megprime-audience">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c9a44a] font-semibold text-sm uppercase tracking-wider">Audience Segmentation</span>
            <h2 className="text-3xl font-bold text-white mt-2">Who We're Reaching</h2>
            <p className="text-blue-200 mt-3 max-w-2xl mx-auto">Three distinct audience tiers, each with tailored messaging and channel strategy.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-[#c9a44a]/50 relative">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-[#c9a44a] text-[#0a1628] text-xs font-bold rounded-full">PRIMARY</div>
              <div className="mt-3">
                <h3 className="text-xl font-bold text-white mb-1">DFW/Houston Renters</h3>
                <p className="text-[#c9a44a] text-sm mb-3">Ages 25-40 | Aspiring Megatel Buyers</p>
                <p className="text-slate-300 text-sm mb-4">Renters who want to build equity from rent payments. They're tired of "throwing money away" and want a path to homeownership.</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-[#c9a44a]" /> Hook: "Your rent could be building equity right now"</div>
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-[#c9a44a]" /> Channel: Instagram Reels, TikTok, Facebook Ads</div>
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-[#c9a44a]" /> CTA: Join Waitlist + RentForward signup</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-blue-400/30 relative">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">SECONDARY</div>
              <div className="mt-3">
                <h3 className="text-xl font-bold text-white mb-1">Megatel Homeowners & Realtors</h3>
                <p className="text-blue-300 text-sm mb-3">Current Buyers + Agent Network</p>
                <p className="text-slate-300 text-sm mb-4">Existing Megatel homeowners who can earn bonus MP through referrals, plus realtors who benefit from the Builders Incentive program.</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-blue-400" /> Hook: "Refer a friend, earn bonus MP tokens"</div>
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-blue-400" /> Channel: Email, LinkedIn, In-community events</div>
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-blue-400" /> CTA: Referral program + MegaLow Rate info</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-green-400/30 relative">
              <div className="absolute -top-3 left-4 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">TERTIARY</div>
              <div className="mt-3">
                <h3 className="text-xl font-bold text-white mb-1">Crypto-Curious Families</h3>
                <p className="text-green-300 text-sm mb-3">Risk-Averse | SEC = Safety Net</p>
                <p className="text-slate-300 text-sm mb-4">Interested in crypto but scared of volatility and scams. The SEC No-Action relief is the trust signal that gets them in the door.</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-green-400" /> Hook: "The first SEC-recognized consumer token"</div>
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-green-400" /> Channel: Facebook, YouTube explainers, LinkedIn</div>
                  <div className="flex items-center gap-2 text-sm text-slate-300"><ArrowRight size={14} className="text-green-400" /> CTA: Education content + Waitlist</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MegPrime Social Media Playbook */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="megprime-playbook">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c9a44a] font-semibold text-sm uppercase tracking-wider">Content Playbook</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Social Media & Content Strategy</h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">A structured 10-12 post/week cadence across all platforms for the first 3 months of launch.</p>
          </div>

          {/* Weekly Content Calendar */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-slate-200 mb-8">
            <h3 className="font-bold text-gray-900 mb-6 text-lg flex items-center gap-2">
              <Calendar size={20} className="text-[#c9a44a]" /> Weekly Content Cadence
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 border border-blue-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">MON</div>
                  <h4 className="font-semibold text-gray-900 text-sm">Reward Reality</h4>
                </div>
                <p className="text-xs text-gray-600">Short Reel showing real spend scenarios. E.g., "$1,200 rent = X MP earned" with animated progress bar to $25K equity goal.</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-green-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">WED</div>
                  <h4 className="font-semibold text-gray-900 text-sm">Educational Carousel</h4>
                </div>
                <p className="text-xs text-gray-600">"How RentForward Works" step-by-step + SEC explainer slides. Swipeable format for Instagram & LinkedIn.</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-pink-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">FRI</div>
                  <h4 className="font-semibold text-gray-900 text-sm">Lifestyle Tie-In</h4>
                </div>
                <p className="text-xs text-gray-600">Megatel lagoon lifestyle photo/video + "Spend at the beach with MP Card rewards." Aspirational content that sells the dream.</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-[#c9a44a]/20 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-[#c9a44a] rounded-lg flex items-center justify-center text-white text-xs font-bold">SUN</div>
                  <h4 className="font-semibold text-gray-900 text-sm">UGC Prompt</h4>
                </div>
                <p className="text-xs text-gray-600">"Tag your rent payment screenshot for a chance at bonus MP." Community-driven engagement that builds social proof.</p>
              </div>
            </div>
          </div>

          {/* Content Types */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Play size={20} className="text-red-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Short-Form Video Series</h4>
              <p className="text-sm text-gray-600 mb-3">"From Rent to Resort" — interview Megatel residents using MegPrime. Real stories, real numbers, real lagoon lifestyle.</p>
              <div className="text-xs text-gray-400">Platforms: Instagram Reels, TikTok, YouTube Shorts</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 size={20} className="text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Infographics & Calculators</h4>
              <p className="text-sm text-gray-600 mb-3">MegaLow Rate savings calculator embedded on site + IG Stories. Visual "spend to reward to home" flow diagrams.</p>
              <div className="text-xs text-gray-400">Platforms: Instagram Stories, Website, Facebook</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Users size={20} className="text-purple-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Daily Stories & Polls</h4>
              <p className="text-sm text-gray-600 mb-3">Waitlist growth milestones, referral contests, polls ("Would you pay rent with crypto if it built equity?").</p>
              <div className="text-xs text-gray-400">Platforms: Instagram & Facebook Stories, X polls</div>
            </div>
          </div>

          {/* Platform Strategy + Growth */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-900 rounded-2xl p-6 text-white">
              <h4 className="font-bold mb-4 flex items-center gap-2"><Globe size={18} className="text-[#c9a44a]" /> Platform Distribution</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2"><Instagram size={16} className="text-pink-400" /> <span className="text-sm">Instagram & TikTok</span></div>
                  <span className="text-xs text-pink-300">Visual lagoon life + Reels</span>
                </div>
                <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2"><Users size={16} className="text-blue-400" /> <span className="text-sm">Facebook</span></div>
                  <span className="text-xs text-blue-300">Older buyers + community groups</span>
                </div>
                <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2"><Linkedin size={16} className="text-sky-400" /> <span className="text-sm">LinkedIn</span></div>
                  <span className="text-xs text-sky-300">Realtors + investors</span>
                </div>
                <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2"><Globe size={16} className="text-slate-400" /> <span className="text-sm">X (@MegPrimePay)</span></div>
                  <span className="text-xs text-slate-300">Crypto community + news</span>
                </div>
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl p-6 text-white">
              <h4 className="font-bold mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-green-400" /> Growth Tactics</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-[#c9a44a] rounded-full flex items-center justify-center text-xs text-[#0a1628] font-bold">1</div>
                    <span className="font-medium text-sm">Referral Program</span>
                  </div>
                  <p className="text-xs text-slate-400 ml-8">Extra MP tokens for sign-ups. Gamified leaderboard for top referrers.</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-[#c9a44a] rounded-full flex items-center justify-center text-xs text-[#0a1628] font-bold">2</div>
                    <span className="font-medium text-sm">Influencer Seeding</span>
                  </div>
                  <p className="text-xs text-slate-400 ml-8">Texas real-estate + personal-finance creators (5-10K followers). Authentic, niche, high-trust audiences.</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-[#c9a44a] rounded-full flex items-center justify-center text-xs text-[#0a1628] font-bold">3</div>
                    <span className="font-medium text-sm">Metrics I'd Track</span>
                  </div>
                  <p className="text-xs text-slate-400 ml-8">Engagement rate, waitlist conversions, link clicks to megprimepay.com, lead volume to sales.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MegPrime Cross-Promotional Campaigns */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0a1628] to-[#122244]" data-testid="megprime-campaigns">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c9a44a] font-semibold text-sm uppercase tracking-wider">Cross-Promotional</span>
            <h2 className="text-3xl font-bold text-white mt-2">Campaigns with Megatel Homes</h2>
            <p className="text-blue-200 mt-3 max-w-2xl mx-auto">High-impact, low-lift campaigns that leverage the Megatel-MegPrime connection — the biggest differentiator.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Campaign 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#c9a44a]/30 hover:border-[#c9a44a]/60 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-[#c9a44a] rounded-xl flex items-center justify-center text-[#0a1628] font-bold text-sm">M1</div>
                <div>
                  <h3 className="font-bold text-white text-sm">Month 1</h3>
                  <p className="text-[#c9a44a] text-xs">Rewards-to-Resort Launch</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">"Pay rent with MP. Earn up to $25K toward your lagoon home."</p>
              <ul className="space-y-2 text-xs text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-[#c9a44a] mt-0.5 flex-shrink-0" /> Hero video + email blast announcing RentForward</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-[#c9a44a] mt-0.5 flex-shrink-0" /> Coordinate photoshoots at model homes (AnaCapri, Bellagio, etc.)</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-[#c9a44a] mt-0.5 flex-shrink-0" /> Families "unlocking" rewards on-site at lagoon communities</li>
              </ul>
            </div>
            {/* Campaign 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-blue-400/30 hover:border-blue-400/60 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white font-bold text-sm">M2</div>
                <div>
                  <h3 className="font-bold text-white text-sm">Month 2</h3>
                  <p className="text-blue-300 text-xs">MegaLow Rate Challenge</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">"See how much you save paying your future mortgage with MPP."</p>
              <ul className="space-y-2 text-xs text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-blue-400 mt-0.5 flex-shrink-0" /> Interactive calculator on Megatel site + social media</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-blue-400 mt-0.5 flex-shrink-0" /> Quiz-style lead gen routed directly to sales reps</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-blue-400 mt-0.5 flex-shrink-0" /> Comparison content: traditional vs. MegaLow Rate mortgage</li>
              </ul>
            </div>
            {/* Campaign 3 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-green-400/30 hover:border-green-400/60 transition-all">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white font-bold text-sm">ON</div>
                <div>
                  <h3 className="font-bold text-white text-sm">Ongoing</h3>
                  <p className="text-green-300 text-xs">Community Events Tie-In</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">Support lagoon opening events with MP branding and live demos.</p>
              <ul className="space-y-2 text-xs text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-green-400 mt-0.5 flex-shrink-0" /> MP Token swag, live reward demos, photo booths</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-green-400 mt-0.5 flex-shrink-0" /> "Warranty + MegPrime Rewards Packet" printed collateral</li>
                <li className="flex items-start gap-2"><CheckCircle size={12} className="text-green-400 mt-0.5 flex-shrink-0" /> QR codes for waitlist signup at model homes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Website & Asset Execution */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="megprime-execution">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[#c9a44a] font-semibold text-sm uppercase tracking-wider">Execution</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Website & Asset Management</h2>
            <p className="text-gray-600 mt-3">The admin ops side of the Specialist role — keeping digital assets consistent and on-brand.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Monitor size={20} className="text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-sm">Weekly Site Updates</h4>
              <p className="text-xs text-gray-600">Updates to megprimepay.com + megatelhomes.com integration (MPP banners, rate calculator embeds, new program pages).</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-10 h-10 bg-[#c9a44a]/20 rounded-lg flex items-center justify-center mb-3">
                <FileText size={20} className="text-[#c9a44a]" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-sm">Inventory & Collateral</h4>
              <p className="text-xs text-gray-600">Order/replenish "MP Token Explained" brochures, QR codes for waitlist at model homes, event materials, and printed packets.</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-sm">Monthly Audits</h4>
              <p className="text-xs text-gray-600">Check all digital assets for compliance/branding consistency. Ensure SEC-related disclaimers are current across all channels.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MegPrime Spend Flow Diagram */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#c9a44a]/10 via-blue-50 to-[#c9a44a]/10" data-testid="megprime-flow">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-xl font-bold text-center text-gray-900 mb-8">The MegPrime Flow: Spend to Reward to Home</h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-2">
            <div className="bg-white rounded-xl p-4 shadow-md border border-slate-200 text-center min-w-[140px]">
              <DollarSign size={24} className="text-[#c9a44a] mx-auto mb-2" />
              <p className="text-sm font-bold text-gray-900">Everyday Spend</p>
              <p className="text-xs text-gray-500">Coffee, rent, bills</p>
            </div>
            <ArrowRight size={24} className="text-[#c9a44a] rotate-90 md:rotate-0" />
            <div className="bg-white rounded-xl p-4 shadow-md border border-[#c9a44a]/30 text-center min-w-[140px]">
              <Zap size={24} className="text-[#c9a44a] mx-auto mb-2" />
              <p className="text-sm font-bold text-gray-900">Earn MP Tokens</p>
              <p className="text-xs text-gray-500">Up to 10% back</p>
            </div>
            <ArrowRight size={24} className="text-[#c9a44a] rotate-90 md:rotate-0" />
            <div className="bg-white rounded-xl p-4 shadow-md border border-blue-200 text-center min-w-[140px]">
              <TrendingUp size={24} className="text-blue-600 mx-auto mb-2" />
              <p className="text-sm font-bold text-gray-900">Build Equity</p>
              <p className="text-xs text-gray-500">RentForward up to $25K</p>
            </div>
            <ArrowRight size={24} className="text-[#c9a44a] rotate-90 md:rotate-0" />
            <div className="bg-gradient-to-br from-[#c9a44a] to-[#a8883e] rounded-xl p-4 shadow-md text-center min-w-[140px]">
              <Home size={24} className="text-white mx-auto mb-2" />
              <p className="text-sm font-bold text-white">Own Your Home</p>
              <p className="text-xs text-white/80">MegaLow Rate: 2% below market</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Calculator Mockup + Lead Capture Flow */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0a1628] via-[#122244] to-[#0a1628] relative overflow-hidden" data-testid="megprime-calculator">
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 20% 30%, #c9a44a 0%, transparent 40%), radial-gradient(circle at 80% 70%, #3b82f6 0%, transparent 40%)'}} />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#c9a44a]/20 border border-[#c9a44a]/30 rounded-full mb-4">
              <span className="text-[#c9a44a] text-xs font-semibold uppercase tracking-wider">Campaign Asset Mockup</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">MegaLow Rate Savings Calculator</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">An interactive tool I'd build for megprimepay.com and social campaigns — users calculate their savings, then enter their info to get a personalized report. Every interaction becomes a qualified lead.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Calculator Panel */}
            <div className="lg:col-span-3 bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Calculator Header */}
              <div className="bg-gradient-to-r from-[#0a1628] to-[#1a3050] p-5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#c9a44a] rounded-lg flex items-center justify-center">
                    <Home size={16} className="text-[#0a1628]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm">MegPrime Savings Calculator</h3>
                    <p className="text-blue-300 text-xs">See how everyday spending builds real home equity</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Sliders */}
                <div className="space-y-6 mb-8">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">Monthly Rent</label>
                      <span className="text-lg font-bold text-[#0a1628]">${calcRent.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min={500}
                      max={4000}
                      step={50}
                      value={calcRent}
                      onChange={(e) => setCalcRent(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#c9a44a]"
                      data-testid="calc-rent-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1"><span>$500</span><span>$4,000</span></div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">Monthly Spending (groceries, bills, etc.)</label>
                      <span className="text-lg font-bold text-[#0a1628]">${calcSpend.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min={200}
                      max={3000}
                      step={50}
                      value={calcSpend}
                      onChange={(e) => setCalcSpend(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#c9a44a]"
                      data-testid="calc-spend-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1"><span>$200</span><span>$3,000</span></div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">Target Home Price</label>
                      <span className="text-lg font-bold text-[#0a1628]">${calcHome.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min={150000}
                      max={750000}
                      step={10000}
                      value={calcHome}
                      onChange={(e) => setCalcHome(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#c9a44a]"
                      data-testid="calc-home-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1"><span>$150K</span><span>$750K</span></div>
                  </div>
                </div>

                {/* Live Results */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-5 border border-slate-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <TrendingUp size={16} className="text-[#c9a44a]" /> Your Projected MegPrime Rewards
                  </h4>
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div className="bg-white rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-gray-500 mb-1">Monthly MP Earned (Rent)</p>
                      <p className="text-xl font-bold text-green-600" data-testid="calc-rent-reward">${Math.round(calcRent * 0.10).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">10% back in MP tokens</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-gray-500 mb-1">Monthly MP Earned (Spending)</p>
                      <p className="text-xl font-bold text-green-600" data-testid="calc-spend-reward">${Math.round(calcSpend * 0.05).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">5% back in MP tokens</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-gray-500 mb-1">RentForward Equity (12 mo)</p>
                      <p className="text-xl font-bold text-[#c9a44a]" data-testid="calc-equity">${Math.min(25000, Math.round(calcRent * 0.10 * 12 + calcSpend * 0.05 * 12)).toLocaleString()}</p>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className="bg-[#c9a44a] h-1.5 rounded-full transition-all duration-500" style={{width: `${Math.min(100, ((calcRent * 0.10 * 12 + calcSpend * 0.05 * 12) / 25000) * 100)}%`}} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">of $25K max</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-100">
                      <p className="text-xs text-gray-500 mb-1">MegaLow Rate Savings</p>
                      <p className="text-xl font-bold text-blue-600" data-testid="calc-mortgage-savings">${Math.round((calcHome * 0.02 / 12) * 360).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">Over 30-yr mortgage (2% lower)</p>
                    </div>
                  </div>

                  {/* Total Impact Summary */}
                  <div className="bg-gradient-to-r from-[#0a1628] to-[#1a3050] rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-300">Your Total Estimated Savings</p>
                        <p className="text-2xl font-bold text-[#c9a44a]" data-testid="calc-total-savings">
                          ${(Math.min(25000, Math.round(calcRent * 0.10 * 12 + calcSpend * 0.05 * 12)) + Math.round((calcHome * 0.02 / 12) * 360)).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-blue-300">Monthly Rate With MegaLow</p>
                        <p className="text-lg font-bold">${Math.round((calcHome * 0.045 / 12)).toLocaleString()}<span className="text-xs text-green-400 ml-1">vs ${Math.round((calcHome * 0.065 / 12)).toLocaleString()}</span></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Gate Mockup */}
                <div className="mt-6 relative">
                  {!calcShowReport ? (
                    <div className="text-center">
                      <button
                        onClick={() => setCalcShowReport(true)}
                        className="px-8 py-3 bg-[#c9a44a] hover:bg-[#b8933f] text-[#0a1628] font-bold rounded-xl transition-all hover:shadow-lg hover:shadow-[#c9a44a]/30 text-sm"
                        data-testid="calc-get-report-btn"
                      >
                        Get My Personalized Report
                      </button>
                      <p className="text-xs text-gray-400 mt-2">See your full savings timeline + home-buying roadmap</p>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200" data-testid="calc-lead-form-mockup">
                      <p className="text-sm font-semibold text-gray-700 mb-3 text-center">Enter your info to unlock your full report</p>
                      <div className="space-y-3">
                        <input type="text" placeholder="Full Name" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#c9a44a]/50 focus:border-[#c9a44a] outline-none" disabled />
                        <input type="email" placeholder="Email Address" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#c9a44a]/50 focus:border-[#c9a44a] outline-none" disabled />
                        <input type="tel" placeholder="Phone Number" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#c9a44a]/50 focus:border-[#c9a44a] outline-none" disabled />
                        <button className="w-full py-3 bg-[#c9a44a] text-[#0a1628] font-bold rounded-lg text-sm cursor-default" disabled>
                          Unlock My Report + Join Waitlist
                        </button>
                      </div>
                      <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs text-blue-600 text-center font-medium">MOCKUP — This form would capture leads to a CRM/database in production</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Lead Capture Flow Explanation */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Target size={18} className="text-[#c9a44a]" /> How the Lead Capture Works
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-[#c9a44a] rounded-full flex items-center justify-center text-[#0a1628] font-bold text-xs flex-shrink-0">1</div>
                    <div>
                      <p className="text-white font-medium text-sm">User Discovers Calculator</p>
                      <p className="text-slate-400 text-xs">Via social ad, megprimepay.com embed, email campaign, or QR code at model homes. No login required to play with sliders.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-[#c9a44a] rounded-full flex items-center justify-center text-[#0a1628] font-bold text-xs flex-shrink-0">2</div>
                    <div>
                      <p className="text-white font-medium text-sm">Interactive Hook</p>
                      <p className="text-slate-400 text-xs">Real-time numbers update as they adjust rent, spending, and home price. They see instant gratification — "$47K+ in savings" builds emotional investment.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-[#c9a44a] rounded-full flex items-center justify-center text-[#0a1628] font-bold text-xs flex-shrink-0">3</div>
                    <div>
                      <p className="text-white font-medium text-sm">Lead Gate Triggers</p>
                      <p className="text-slate-400 text-xs">"Get My Personalized Report" button — they've already seen the value, now they trade name + email + phone for the full breakdown + savings timeline.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-[#c9a44a] rounded-full flex items-center justify-center text-[#0a1628] font-bold text-xs flex-shrink-0">4</div>
                    <div>
                      <p className="text-white font-medium text-sm">Lead Captured to CRM</p>
                      <p className="text-slate-400 text-xs">Name, email, phone, plus their calculator inputs (rent, spend, home price) stored in database. Sales team gets a warm lead with budget context.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">5</div>
                    <div>
                      <p className="text-white font-medium text-sm">Automated Follow-Up</p>
                      <p className="text-slate-400 text-xs">Personalized email with their full report + savings timeline. CRM tags lead as "calculator-qualified." Drip campaign begins with RentForward/MegaLow content.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribution Channels */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Globe size={18} className="text-blue-400" /> Distribution Strategy
                </h3>
                <div className="space-y-3">
                  <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                    <Monitor size={16} className="text-blue-400" />
                    <div>
                      <p className="text-white text-sm font-medium">megprimepay.com</p>
                      <p className="text-slate-400 text-xs">Embedded on homepage + dedicated /calculator page</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                    <Instagram size={16} className="text-pink-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Social Ads (IG/FB/TikTok)</p>
                      <p className="text-slate-400 text-xs">"See how much you'd save" CTA → link in bio / direct link</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                    <Mail size={16} className="text-[#c9a44a]" />
                    <div>
                      <p className="text-white text-sm font-medium">Email Campaigns</p>
                      <p className="text-slate-400 text-xs">Waitlist follow-up: "Calculate your savings with MegPrime"</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                    <Home size={16} className="text-green-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Model Home QR Codes</p>
                      <p className="text-slate-400 text-xs">Physical signage at lagoon communities → scan to calculate</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Projected Metrics */}
              <div className="bg-gradient-to-br from-[#c9a44a]/20 to-[#c9a44a]/5 rounded-2xl p-6 border border-[#c9a44a]/30">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <BarChart3 size={18} className="text-[#c9a44a]" /> Projected Lead Metrics
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-[#c9a44a]">15-25%</p>
                    <p className="text-xs text-slate-300">Calculator-to-lead conversion</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-[#c9a44a]">3-5x</p>
                    <p className="text-xs text-slate-300">Higher intent vs cold leads</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-[#c9a44a]">$2-5</p>
                    <p className="text-xs text-slate-300">Estimated CPL via social</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-[#c9a44a]">48hr</p>
                    <p className="text-xs text-slate-300">Avg time to sales follow-up</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section - Updated with MegPrime tools */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Tools & Platforms I'd Use</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {['Meta Business Suite', 'Google Ads', 'Later (Auto-Posting)', 'Canva', 'CapCut', 'Google Analytics', 'SEMrush', 'ChatGPT (Captions)', 'Hootsuite', 'WTS Social Builder', 'WTS Turbo SEO', 'WTS Auto Blog Generation', 'WordPress (megprimepay.com)', 'Sprout Social', 'Adobe Creative Suite'].map(tool => (
              <span key={tool} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">{tool}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Me Section - Updated with MegPrime */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why I'm Excited About Megatel & MegPrime</h2>
          <p className="text-lg text-gray-600 mb-4">
            Megatel isn't just building houses — you're creating <strong>lifestyle experiences</strong> that most people only dream about. And now with MegPrime, you're not just selling homes — you're building a <strong>financial ecosystem</strong> that makes homeownership actually attainable for everyday people.
          </p>
          <p className="text-base text-gray-500 mb-8">
            The lagoon communities, the founders' story, RentForward, the SEC milestone — this is the kind of story that <strong>markets itself</strong> when told right. I want to be the one telling it.
          </p>
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <h3 className="font-bold text-gray-900 mb-4">My Relevant Experience</h3>
            <div className="grid md:grid-cols-4 gap-6 text-left">
              <div>
                <div className="text-3xl font-bold text-slate-700 mb-2">850K</div>
                <p className="text-sm text-gray-600">Campaign reach with 200+ qualified leads and 75% cost reduction</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-700 mb-2">500K+</div>
                <p className="text-sm text-gray-600">TikTok views driving real conversions (Fort Worth Dental)</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-700 mb-2">127K</div>
                <p className="text-sm text-gray-600">Video views with 68% foot-traffic lift from content campaigns</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#c9a44a] mb-2">5+ Yrs</div>
                <p className="text-sm text-gray-600">Social media strategy, video production, & data-driven marketing</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Updated */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#0a1628] via-[#122244] to-[#0a1628]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Let's Build the MegPrime Story Together</h2>
          <p className="text-xl text-blue-200 mb-8">I'd love to discuss how I can help Megatel Homes and MegPrime reach more homebuyers and turn everyday spending into real homeownership.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:me@sierrascala.com" className="px-8 py-3 bg-[#c9a44a] text-[#0a1628] rounded-full font-semibold hover:shadow-lg hover:shadow-[#c9a44a]/30 transition-all flex items-center gap-2">
              <Mail size={20} /> me@sierrascala.com
            </a>
            <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-all flex items-center gap-2">
              <Linkedin size={20} /> Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900 text-center">
        <p className="text-slate-400 text-sm">
          © {new Date().getFullYear()} Sierra Scala | <a href="/" className="text-blue-400 hover:text-blue-300">View Full Portfolio</a>
        </p>
      </footer>
    </div>
  );
};

// Check if we're on the Megatel landing page

export default MegatelLandingPage;
